import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CandidateCardComponent } from './candidate-card.component';
import { CandidateCardModel, CandidateSelectionValue } from './candidate-card.models';

const BLANK_VOTE_CARD_ID = null;

@Component({
  selector: 'app-candidate-grid',
  standalone: true,
  imports: [CommonModule, CandidateCardComponent],
  templateUrl: './candidate-grid.component.html',
  styleUrls: ['./candidate-grid.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CandidateGridComponent implements OnChanges {
  @Input({ required: true }) candidates: CandidateCardModel[] = [];
  @Input() selectionEnabled = false;
  @Input() selectionDisabled = false;
  @Input() maxSelectable = 0;
  @Input() selectedCandidateIds: CandidateSelectionValue[] = [];
  @Input() blankVotesCount = 0;
  @Input() blankVotesPercentage: number | null = null;

  @Output() selectedCandidateIdsChange = new EventEmitter<CandidateSelectionValue[]>();
  @Output() selectionLimitReached = new EventEmitter<string>();

  currentSelectedCandidateIds: CandidateSelectionValue[] = [];

  get displayCandidates(): CandidateCardModel[] {
    if (!this.shouldShowBlankVoteCard) {
      return this.candidates;
    }

    return [...this.candidates, this.buildBlankVoteCard()];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['selectedCandidateIds']) {
      return;
    }

    this.currentSelectedCandidateIds = [...this.selectedCandidateIds];
  }

  isCandidateSelected(candidateId: CandidateSelectionValue): boolean {
    return this.currentSelectedCandidateIds.includes(candidateId);
  }

  handleCandidateClick(candidateId: CandidateSelectionValue): void {
    if (!this.selectionEnabled || this.selectionDisabled) {
      return;
    }

    if (this.isCandidateSelected(candidateId)) {
      this.currentSelectedCandidateIds = this.currentSelectedCandidateIds.filter((id) => id !== candidateId);
      this.selectedCandidateIdsChange.emit(this.currentSelectedCandidateIds);
      return;
    }

    if (candidateId === null) {
      this.currentSelectedCandidateIds = [null];
      this.selectedCandidateIdsChange.emit(this.currentSelectedCandidateIds);
      return;
    }

    this.currentSelectedCandidateIds = this.currentSelectedCandidateIds.filter((id) => id !== null);

    if (this.maxSelectable === 1) {
      this.currentSelectedCandidateIds = [candidateId];
      this.selectedCandidateIdsChange.emit(this.currentSelectedCandidateIds);
      return;
    }

    if (this.currentSelectedCandidateIds.length >= this.maxSelectable) {
      this.selectionLimitReached.emit(
        `Você já selecionou o limite de ${this.maxSelectable} candidatos permitido nesta eleição.`,
      );
      return;
    }

    this.currentSelectedCandidateIds = [...this.currentSelectedCandidateIds, candidateId];
    this.selectedCandidateIdsChange.emit(this.currentSelectedCandidateIds);
  }

  getCandidateSelectionValue(candidate: CandidateCardModel): CandidateSelectionValue {
    return candidate.selectionValue ?? candidate.id;
  }

  private get shouldShowBlankVoteCard(): boolean {
    return this.selectionEnabled || this.blankVotesCount > 0;
  }

  private buildBlankVoteCard(): CandidateCardModel {
    return {
      id: BLANK_VOTE_CARD_ID,
      candidateNumber: 'Em branco',
      name: 'Voto em branco',
      department: '',
      position: this.selectionEnabled ? '' : 'Apuração consolidada',
      slogan: this.selectionEnabled
        ? 'Selecione esta opção se preferir não indicar nenhum candidato nesta eleição.'
        : 'Participações registradas sem escolha de candidatos.',
      totalVotes: this.selectionEnabled ? null : this.blankVotesCount,
      votePercentage: this.selectionEnabled ? null : this.blankVotesPercentage,
      selectionValue: null,
      isBlankVoteOption: true,
      isElectionActive: this.candidates.some((candidate) => candidate.isElectionActive),
      isElected: false,
    };
  }
}
